<template>
  <div class="formula-editor" ref="editorWrapper">
    <div class="formula-input-row">
      <span class="formula-prefix">=</span>
      <div class="formula-input-wrapper" ref="inputWrapper">
        <textarea
          ref="textarea"
          class="formula-textarea"
          :value="modelValue"
          @input="onInput"
          @keydown="onKeydown"
          @click="updateCaretContext"
          @scroll="syncScroll"
          rows="3"
          spellcheck="false"
          placeholder="e.g. SUM({price}, {tax}) or {grandparent.child.number}"
        />
      </div>
    </div>

    <div v-if="showSuggestions && filteredSuggestions.length" class="suggestions-dropdown" ref="dropdown">
      <div class="suggestions-header">
        <span v-if="suggestionContext === 'function'" class="suggestions-context-label">Functions</span>
        <span v-else-if="suggestionContext === 'field'" class="suggestions-context-label">Fields</span>
        <span v-else-if="suggestionContext === 'relatedField'" class="suggestions-context-label">Related Fields</span>
        <span v-else-if="suggestionContext === 'reverseRelColumn'" class="suggestions-context-label">Relationships</span>
        <span v-else-if="suggestionContext === 'reverseRelField'" class="suggestions-context-label">Related Fields</span>
        <span class="suggestions-count">{{ filteredSuggestions.length }} result{{ filteredSuggestions.length === 1 ? '' : 's' }}</span>
      </div>
      <div class="suggestions-list" ref="suggestionsList">
        <div
          v-for="(suggestion, idx) in filteredSuggestions"
          :key="suggestion.key"
          class="suggestion-item"
          :class="{ active: idx === selectedIndex }"
          @mousedown.prevent="acceptSuggestion(idx)"
          @mouseenter="selectedIndex = idx"
          ref="suggestionItems"
        >
          <span class="suggestion-badge" :class="'badge-' + suggestion.type">{{ suggestion.badge }}</span>
          <span class="suggestion-name">{{ suggestion.display }}</span>
          <span v-if="suggestion.detail" class="suggestion-detail">{{ suggestion.detail }}</span>
        </div>
      </div>
      <div v-if="activeSuggestionDescription" class="suggestion-description">
        <div class="suggestion-description-sig">{{ activeSuggestionSignature }}</div>
        <div class="suggestion-description-text">{{ activeSuggestionDescription }}</div>
      </div>
    </div>

    <div class="formula-toolbar">
      <span class="toolbar-hint">
        <kbd>{</kbd> fields
        <kbd>{rel.</kbd> related
        <kbd>{sheet.rel.</kbd> reverse
        <span class="toolbar-sep">|</span>
        <kbd>Tab</kbd> / <kbd>Enter</kbd> accept
        <kbd>Esc</kbd> dismiss
      </span>
    </div>
  </div>
</template>

<script>
import { FORMULA_FUNCTIONS, CATEGORY_ICONS } from '../lib/formulaFunctions.js'

export default {
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    columns: {
      type: Array,
      default: () => []
    },
    spreadsheets: {
      type: Array,
      default: () => []
    },
    currentSpreadsheetUniqueTitle: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue'],
  data () {
    return {
      showSuggestions: false,
      selectedIndex: 0,
      suggestionContext: 'function',
      currentToken: '',
      caretPosition: 0
    }
  },
  computed: {
    relationshipColumns () {
      return this.columns.filter(c => c.type === 'relationship')
    },

    reverseRelationshipsBySpreadsheet () {
      if (!this.currentSpreadsheetUniqueTitle) return {}
      const bySheet = {}
      for (const ss of this.spreadsheets) {
        if (ss.uniqueTitle === this.currentSpreadsheetUniqueTitle) continue
        if (!ss.columns) continue
        for (const col of ss.columns) {
          if (col.type === 'relationship' && col.relatedSpreadSheet === this.currentSpreadsheetUniqueTitle) {
            if (!bySheet[ss.uniqueTitle]) bySheet[ss.uniqueTitle] = []
            bySheet[ss.uniqueTitle].push(col)
          }
        }
      }
      return bySheet
    },

    reverseSpreadsheetNames () {
      return Object.keys(this.reverseRelationshipsBySpreadsheet)
    },

    systemFieldSuggestions () {
      return [
        { uniqueTitle: 'id', title: 'ID', type: 'number' },
        { uniqueTitle: 'createdAt', title: 'Created At', type: 'dateTime' },
        { uniqueTitle: 'updatedAt', title: 'Updated At', type: 'dateTime' }
      ].map(c => ({
        key: 'field:' + c.uniqueTitle,
        type: 'field',
        badge: c.type === 'number' ? '#' : 'dt',
        display: c.uniqueTitle,
        detail: c.title,
        insertText: c.uniqueTitle + '}',
        description: `System field "${c.title}" (${c.type})`,
        signature: '{' + c.uniqueTitle + '}'
      }))
    },

    fieldSuggestions () {
      const userFields = this.columns
        .filter(c => c.type !== 'relationship')
        .map(c => ({
          key: 'field:' + c.uniqueTitle,
          type: 'field',
          badge: c.type === 'number' ? '#' : c.type === 'dateTime' ? 'dt' : 'T',
          display: c.uniqueTitle,
          detail: c.title !== c.uniqueTitle ? c.title : '',
          insertText: c.uniqueTitle + '}',
          description: `Field "${c.title}" (${c.type})`,
          signature: '{' + c.uniqueTitle + '}'
        }))
      return [...this.systemFieldSuggestions, ...userFields]
    },

    relationshipSuggestions () {
      const forward = this.relationshipColumns.map(c => ({
        key: 'rel:' + c.uniqueTitle,
        type: 'relationship',
        badge: 'rel',
        display: c.uniqueTitle + '.',
        detail: `→ ${c.relatedSpreadSheet || '?'}`,
        insertText: c.uniqueTitle + '.',
        description: `Relationship to "${c.relatedSpreadSheet || '?'}" — type to pick a field`,
        signature: '{' + c.uniqueTitle + '.field}'
      }))
      const reverse = this.reverseSpreadsheetNames.map(ssName => ({
        key: 'revss:' + ssName,
        type: 'reverseRel',
        badge: '←',
        display: ssName + '.',
        detail: `← has relationship to here`,
        insertText: ssName + '.',
        description: `Spreadsheet "${ssName}" has relationships pointing here — type to pick one`,
        signature: '{' + ssName + '.relationship.field}'
      }))
      return [...forward, ...reverse]
    },

    functionSuggestions () {
      return FORMULA_FUNCTIONS.map(f => ({
        key: 'fn:' + f.name,
        type: 'function',
        badge: CATEGORY_ICONS[f.category] || 'fn',
        display: f.name,
        detail: f.category,
        insertText: f.name + '(',
        description: f.description,
        signature: f.signature
      }))
    },

    filteredSuggestions () {
      const token = this.currentToken.toLowerCase()
      if (!token && this.suggestionContext === 'function') return []

      let pool
      if (this.suggestionContext === 'function') {
        pool = this.functionSuggestions
      } else if (this.suggestionContext === 'field') {
        pool = [...this.fieldSuggestions, ...this.relationshipSuggestions]
      } else if (this.suggestionContext === 'relatedField') {
        pool = this.forwardRelFieldPool
      } else if (this.suggestionContext === 'reverseRelColumn') {
        pool = this.reverseRelColumnPool
      } else if (this.suggestionContext === 'reverseRelField') {
        pool = this.reverseRelFieldPool
      } else {
        return []
      }

      if (!token) return pool.slice(0, 50)

      const starts = []
      const contains = []
      for (const s of pool) {
        const lower = s.display.toLowerCase()
        if (lower.startsWith(token)) {
          starts.push(s)
        } else if (lower.includes(token)) {
          contains.push(s)
        }
      }
      return [...starts, ...contains].slice(0, 50)
    },

    forwardRelFieldPool () {
      const parsed = this.parseBraceContent()
      if (!parsed || parsed.parts.length !== 2) return []
      const relName = parsed.parts[0]

      const forwardCol = this.relationshipColumns.find(c => c.uniqueTitle === relName)
      if (!forwardCol || !forwardCol.relatedSpreadSheet) return []
      const relSpreadsheet = this.spreadsheets.find(s => s.uniqueTitle === forwardCol.relatedSpreadSheet)
      if (!relSpreadsheet || !relSpreadsheet.columns) return []

      const systemFields = [
        { uniqueTitle: 'id', title: 'ID', type: 'number' },
        { uniqueTitle: 'createdAt', title: 'Created At', type: 'dateTime' },
        { uniqueTitle: 'updatedAt', title: 'Updated At', type: 'dateTime' }
      ]
      const allColumns = [...systemFields, ...relSpreadsheet.columns]

      return allColumns
        .filter(c => c.type !== 'relationship')
        .map(c => ({
          key: 'relf:' + relName + '.' + c.uniqueTitle,
          type: 'relatedField',
          badge: c.type === 'number' ? '#' : c.type === 'dateTime' ? 'dt' : 'T',
          display: c.uniqueTitle,
          detail: c.title !== c.uniqueTitle ? c.title : '',
          insertText: c.uniqueTitle + '}',
          description: `Field "${c.title}" from "${relSpreadsheet.title}" (${c.type})`,
          signature: '{' + relName + '.' + c.uniqueTitle + '}'
        }))
    },

    reverseRelColumnPool () {
      const parsed = this.parseBraceContent()
      if (!parsed || parsed.parts.length !== 2) return []
      const ssName = parsed.parts[0]

      const relCols = this.reverseRelationshipsBySpreadsheet[ssName]
      if (!relCols || !relCols.length) return []

      return relCols.map(c => ({
        key: 'revrelcol:' + ssName + '.' + c.uniqueTitle,
        type: 'reverseRel',
        badge: '←rel',
        display: c.uniqueTitle + '.',
        detail: c.title !== c.uniqueTitle ? c.title : '',
        insertText: c.uniqueTitle + '.',
        description: `Relationship "${c.title}" on "${ssName}" — type to pick a field`,
        signature: '{' + ssName + '.' + c.uniqueTitle + '.field}'
      }))
    },

    reverseRelFieldPool () {
      const parsed = this.parseBraceContent()
      if (!parsed || parsed.parts.length !== 3) return []
      const ssName = parsed.parts[0]

      const ownerSpreadsheet = this.spreadsheets.find(s => s.uniqueTitle === ssName)
      if (!ownerSpreadsheet || !ownerSpreadsheet.columns) return []

      const systemFields = [
        { uniqueTitle: 'id', title: 'ID', type: 'number' },
        { uniqueTitle: 'createdAt', title: 'Created At', type: 'dateTime' },
        { uniqueTitle: 'updatedAt', title: 'Updated At', type: 'dateTime' }
      ]
      const allColumns = [...systemFields, ...ownerSpreadsheet.columns]

      return allColumns
        .filter(c => c.type !== 'relationship')
        .map(c => ({
          key: 'revrelf:' + ssName + '.' + parsed.parts[1] + '.' + c.uniqueTitle,
          type: 'relatedField',
          badge: c.type === 'number' ? '#' : c.type === 'dateTime' ? 'dt' : 'T',
          display: c.uniqueTitle,
          detail: c.title !== c.uniqueTitle ? c.title : '',
          insertText: c.uniqueTitle + '}',
          description: `Field "${c.title}" from "${ownerSpreadsheet.title}" (reverse rel) (${c.type})`,
          signature: '{' + ssName + '.' + parsed.parts[1] + '.' + c.uniqueTitle + '}'
        }))
    },

    activeSuggestionDescription () {
      const s = this.filteredSuggestions[this.selectedIndex]
      return s ? s.description : ''
    },
    activeSuggestionSignature () {
      const s = this.filteredSuggestions[this.selectedIndex]
      return s ? s.signature : ''
    }
  },
  methods: {
    onInput (e) {
      this.$emit('update:modelValue', e.target.value)
      this.$nextTick(() => {
        this.updateCaretContext()
      })
    },

    parseBraceContent () {
      const el = this.$refs.textarea
      if (!el) return null
      const text = el.value
      const before = text.substring(0, this.caretPosition)
      const braceIdx = before.lastIndexOf('{')
      const closeBrace = before.lastIndexOf('}')
      if (braceIdx <= closeBrace) return null
      const insideBrace = before.substring(braceIdx + 1)
      const parts = insideBrace.split('.')
      return { insideBrace, parts }
    },

    updateCaretContext () {
      const el = this.$refs.textarea
      if (!el) return
      this.caretPosition = el.selectionStart
      const text = el.value
      const before = text.substring(0, this.caretPosition)

      const braceIdx = before.lastIndexOf('{')
      const closeBrace = before.lastIndexOf('}')

      if (braceIdx > closeBrace) {
        const insideBrace = before.substring(braceIdx + 1)
        const parts = insideBrace.split('.')

        if (parts.length === 1) {
          this.suggestionContext = 'field'
          this.currentToken = parts[0]
        } else if (parts.length === 2) {
          const prefix = parts[0]
          const isForwardRel = this.relationshipColumns.some(c => c.uniqueTitle === prefix)
          const isReverseSpreadsheet = this.reverseSpreadsheetNames.includes(prefix)

          if (isForwardRel) {
            this.suggestionContext = 'relatedField'
            this.currentToken = parts[1]
          } else if (isReverseSpreadsheet) {
            this.suggestionContext = 'reverseRelColumn'
            this.currentToken = parts[1]
          } else {
            this.suggestionContext = 'relatedField'
            this.currentToken = parts[1]
          }
        } else if (parts.length === 3) {
          this.suggestionContext = 'reverseRelField'
          this.currentToken = parts[2]
        } else {
          this.showSuggestions = false
          this.currentToken = ''
          return
        }

        this.showSuggestions = true
        this.clampSelectedIndex()
        return
      }

      const wordMatch = before.match(/([A-Za-z_]\w*)$/)
      if (wordMatch) {
        this.suggestionContext = 'function'
        this.currentToken = wordMatch[1]
        this.showSuggestions = true
        this.clampSelectedIndex()
        return
      }

      this.showSuggestions = false
      this.currentToken = ''
    },

    onKeydown (e) {
      if (!this.showSuggestions || !this.filteredSuggestions.length) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.selectedIndex = (this.selectedIndex + 1) % this.filteredSuggestions.length
        this.scrollToSelected()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        this.selectedIndex = (this.selectedIndex - 1 + this.filteredSuggestions.length) % this.filteredSuggestions.length
        this.scrollToSelected()
      } else if (e.key === 'Tab' || e.key === 'Enter') {
        if (this.filteredSuggestions.length > 0) {
          e.preventDefault()
          this.acceptSuggestion(this.selectedIndex)
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        this.showSuggestions = false
      }
    },

    acceptSuggestion (idx) {
      const suggestion = this.filteredSuggestions[idx]
      if (!suggestion) return

      const el = this.$refs.textarea
      const text = el.value
      const caret = this.caretPosition

      let replaceStart, insertText

      if (this.suggestionContext === 'field') {
        const braceIdx = text.lastIndexOf('{', caret - 1)
        replaceStart = braceIdx + 1
        insertText = suggestion.insertText
      } else if (this.suggestionContext === 'relatedField' || this.suggestionContext === 'reverseRelColumn' || this.suggestionContext === 'reverseRelField') {
        const braceIdx = text.lastIndexOf('{', caret - 1)
        const insideBrace = text.substring(braceIdx + 1, caret)
        const lastDotIdx = insideBrace.lastIndexOf('.')
        replaceStart = braceIdx + 1 + lastDotIdx + 1
        insertText = suggestion.insertText
      } else {
        const before = text.substring(0, caret)
        const wordMatch = before.match(/([A-Za-z_]\w*)$/)
        replaceStart = wordMatch ? caret - wordMatch[1].length : caret
        insertText = suggestion.insertText
      }

      const newText = text.substring(0, replaceStart) + insertText + text.substring(caret)
      this.$emit('update:modelValue', newText)

      const newCaret = replaceStart + insertText.length
      this.$nextTick(() => {
        el.value = newText
        el.selectionStart = newCaret
        el.selectionEnd = newCaret
        el.focus()
        this.caretPosition = newCaret
        this.updateCaretContext()
      })
    },

    clampSelectedIndex () {
      if (this.selectedIndex >= this.filteredSuggestions.length) {
        this.selectedIndex = 0
      }
    },

    scrollToSelected () {
      this.$nextTick(() => {
        const items = this.$refs.suggestionItems
        if (!items || !items[this.selectedIndex]) return
        items[this.selectedIndex].scrollIntoView({ block: 'nearest' })
      })
    },

    syncScroll () {
      // placeholder for future highlight layer sync
    }
  },
  watch: {
    filteredSuggestions () {
      this.clampSelectedIndex()
    }
  }
}
</script>

<style scoped>
.formula-editor {
  position: relative;
  border: 1px solid #3a3a37;
  border-radius: 6px;
  background: #141412;
  overflow: visible;
}

.formula-input-row {
  display: flex;
  align-items: flex-start;
}

.formula-prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  min-height: 60px;
  background: #1a1a17;
  color: #9ad12b;
  font-weight: 700;
  font-size: 1rem;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  border-right: 1px solid #2e2e2b;
  border-radius: 6px 0 0 0;
  user-select: none;
  flex-shrink: 0;
}

.formula-input-wrapper {
  flex: 1;
  position: relative;
}

.formula-textarea {
  width: 100%;
  min-height: 60px;
  padding: 8px 10px;
  background: transparent;
  border: none;
  color: #e8e8e6;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 0.85rem;
  line-height: 1.5;
  resize: vertical;
  outline: none;
}
.formula-textarea::placeholder {
  color: #4a4a47;
  font-style: italic;
}
.formula-textarea:focus {
  background: #181916;
}

/* Suggestions dropdown */
.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #1e1e1b;
  border: 1px solid #3a3a37;
  border-top: none;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.5);
  max-height: 320px;
  display: flex;
  flex-direction: column;
}

.suggestions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 10px;
  border-bottom: 1px solid #2a2a27;
  font-size: 0.7rem;
}
.suggestions-context-label {
  color: #9ad12b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.suggestions-count {
  color: #4a4a47;
}

.suggestions-list {
  overflow-y: auto;
  max-height: 200px;
  flex: 1;
}

.suggestion-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 0.82rem;
  transition: background 0.08s;
}
.suggestion-item:hover,
.suggestion-item.active {
  background: #2a2a27;
}

.suggestion-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 18px;
  border-radius: 3px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  flex-shrink: 0;
}
.badge-function {
  background: #2a3a1a;
  color: #9ad12b;
}
.badge-field {
  background: #1a2a3a;
  color: #5ba0d0;
}
.badge-relationship {
  background: #3a2a1a;
  color: #d0a05b;
}
.badge-reverseRel {
  background: #2a1a3a;
  color: #a05bd0;
}
.badge-relatedField {
  background: #2a1a3a;
  color: #a05bd0;
}

.suggestion-name {
  color: #e8e8e6;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-weight: 500;
}
.suggestion-detail {
  margin-left: auto;
  color: #4a4a47;
  font-size: 0.72rem;
  white-space: nowrap;
}

.suggestion-description {
  border-top: 1px solid #2a2a27;
  padding: 6px 10px;
  font-size: 0.75rem;
  background: #1a1a17;
  border-radius: 0 0 6px 6px;
}
.suggestion-description-sig {
  color: #9ad12b;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-weight: 600;
  margin-bottom: 2px;
}
.suggestion-description-text {
  color: #8a8a87;
  line-height: 1.4;
}

/* Toolbar */
.formula-toolbar {
  padding: 4px 10px;
  border-top: 1px solid #2a2a27;
  background: #141412;
  border-radius: 0 0 6px 6px;
}
.toolbar-hint {
  font-size: 0.68rem;
  color: #4a4a47;
}
.toolbar-hint kbd {
  display: inline-block;
  padding: 0 4px;
  border: 1px solid #3a3a37;
  border-radius: 3px;
  background: #1e1e1b;
  color: #7a7a78;
  font-size: 0.65rem;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  margin: 0 1px;
}
.toolbar-sep {
  margin: 0 6px;
  color: #2a2a27;
}
</style>
